import {
    type ApiResult,
    type BaseParams,
    type CreateInviteData,
    type CreateInviteParam,
    type EmojiId,
    type GetGroupInvitesResponse,
    type GroupParam,
    type InviteListParam,
    type InviteParam,
    links,
    type PostParams,
    Request,
    type ShowInviteData,
    type ShowInviteParam,
    type UpdateInviteParam,
} from '@app/client';
import { apiFactory } from '@app/client/apiFactory/api';
import { type SocialGroup } from '@app/gen';

type InviteMemberParams = BaseParams & PostParams<InviteParam>;
type UpdateInviteParams = BaseParams & UpdateInviteParam;
type InviteUserParams = BaseParams & CreateInviteParam;
type InviteShowParams = BaseParams & ShowInviteParam;

const inviteMember = async (params: InviteMemberParams): Promise<ApiResult<SocialGroup>> =>
    Request.postRequest<SocialGroup>({
        ...params,
        target: links.pods.single(params.value.groupId).invite().send(),
        value: params.value.memberIds,
    });

export const callInviteMember = (params: BaseParams & InviteParam): Promise<SocialGroup> =>
    typeof params.groupId === 'undefined'
        ? Promise.reject(new Error('Invalid groupId'))
        : apiFactory()
              .call(inviteMember, {
                  ...params,
                  value: {
                      groupId: params.groupId,
                      memberIds: params.memberIds,
                  },
              })
              .then((result) => {
                  if (result.ok) {
                      return result.value;
                  }
                  const errorMessage = result?.error?.detail ?? `Error inviting member to group ${params.groupId}`;
                  throw new Error(errorMessage);
              });

const acceptInvite = async (params: UpdateInviteParam): Promise<ApiResult<SocialGroup>> =>
    Request.postRequest<SocialGroup>({
        ...params,
        target: links.pods.single(params.groupId).invite().accept(params.inviteId),
        value: { ...params },
    });

export const callAcceptInvite = (params: UpdateInviteParams): Promise<SocialGroup> =>
    typeof params.groupId === 'undefined'
        ? Promise.reject(new Error('Invalid groupId'))
        : apiFactory()
              .call(acceptInvite, {
                  ...params,

                  groupId: params.groupId,
                  inviteId: params.inviteId,
              })
              .then((result) => {
                  if (result.ok) {
                      return result.value;
                  }
                  const errorMessage = result?.error?.detail ?? `Error accepting invite to group ${params.groupId}`;
                  throw new Error(errorMessage);
              });

// TODO: Remove this invite if we are retiring the old invite process
const getGroupInvites = (params: InviteListParam): Promise<ApiResult<GetGroupInvitesResponse>> =>
    Request.getRequest<GetGroupInvitesResponse>({
        ...params,
        target: links.pods.single(params.groupId).invites(),
    });

export const callGetGroupInvites = (
    params: BaseParams & GroupParam,
    isAuthenticated = true,
): Promise<GetGroupInvitesResponse> =>
    typeof params.groupId === 'undefined'
        ? Promise.reject(new Error('Invalid groupId'))
        : apiFactory()
              .call(getGroupInvites, {
                  ...params,
                  groupId: params.groupId,
                  settings: {
                      ...params.settings,
                      credentials: isAuthenticated ? 'include' : 'omit',
                  },
              })
              .then((result) => {
                  if (result.ok) {
                      return result.value;
                  }
                  const errorMessage = result?.error?.detail ?? `Error getting invites for group ${params.groupId}`;
                  throw new Error(errorMessage);
              });

const removeMemberInvite = (params: UpdateInviteParams): Promise<ApiResult<SocialGroup>> => {
    return Request.deleteRequest<SocialGroup>({
        ...params,
        target: links.pods.single(params.groupId).invite().remove(params.inviteId),
        value: { ...params },
    });
};

export const callRemoveMemberInvite = (params: UpdateInviteParams): Promise<SocialGroup> => {
    return apiFactory()
        .call(removeMemberInvite, {
            ...params,
            id: params.groupId,
            inviteId: params.inviteId,
        })
        .then((result) => {
            if (result.ok) {
                return result.value;
            }
            const errorMessage = result?.error?.detail ?? 'Error removing invite';
            throw new Error(errorMessage);
        });
};

const createInvite = (params: InviteUserParams): Promise<ApiResult<CreateInviteData>> => {
    return Request.postRequest<CreateInviteData>({
        ...params,
        target: links.invites().create(),
        value: {
            ...params,
        },
    });
};

export const callCreateInvite = (params: InviteUserParams): Promise<CreateInviteData> => {
    return apiFactory()
        .call(createInvite, {
            ...params,
            group_id: params.group_id,
        })
        .then((result) => {
            if (result.ok) {
                return result.value;
            }
            const errorMessage = result?.error?.detail ?? 'Error creating invite';
            throw new Error(errorMessage);
        });
};

const showInvite = (params: InviteShowParams): Promise<ApiResult<ShowInviteData>> => {
    return Request.getRequest<ShowInviteData>({
        ...params,
        target: links.invites().single(params.inviteCode).show(),
        settings: {
            ...params.settings,
            credentials: 'omit',
        },
    });
};

export const callShowInvite = (params: InviteShowParams): Promise<ShowInviteData> => {
    return apiFactory()
        .call(showInvite, { ...params, inviteCode: params.inviteCode })
        .then((result) => {
            if (result.ok) {
                return result.value;
            }
            const errorMessage = result?.error?.detail ?? 'Error showing invite data';
            throw new Error(errorMessage);
        });
};

const getFreeYat = (params: BaseParams = {}): Promise<ApiResult<EmojiId[]>> => {
    return Request.getRequest<EmojiId[]>({
        ...params,
        target: links.freeYat(),
        settings: {
            ...params.settings,
            credentials: 'omit',
        },
    });
};

export const callGetFreeYat = (params: BaseParams = {}): Promise<EmojiId[]> => {
    return apiFactory()
        .call(getFreeYat, params)
        .then((result) => {
            if (result.ok) {
                return result.value;
            }
            const errorMessage = result?.error?.detail ?? 'Error getting free yat';
            throw new Error(errorMessage);
        });
};

export const acceptInviteByCode = (
    params: BaseParams & PostParams<{ inviteCode: string }>,
): Promise<ApiResult<null>> => {
    return Request.postRequest<null>({
        ...params,
        target: links.invites().accept(params.value.inviteCode),
    });
};

export const callAcceptInviteByCode = (inviteCode: string) => {
    return apiFactory()
        .call(acceptInviteByCode, { value: { inviteCode } })
        .then((result) => {
            if (result.ok) {
                return result.value;
            }
            const errorMessage = result?.error?.detail ?? 'Error accepting invite';
            throw new Error(errorMessage);
        });
};
